import { Field, Text } from "@sitecore-jss/sitecore-jss-react";
import React, { useCallback, useEffect, useState } from "react";

import { Button } from "../../components/buttons/Button";
import { Coverage, getCoverageId } from "../../components/dlp/calculation/Coverage";
import { InsuredPersons } from "../../components/dlp/calculation/InsuredPersons";
import { DlpSummary } from "../../components/dlp/DlpSummary";
import { Step } from "../../components/stepIndicator/Step";
import { StepIndicator } from "../../components/stepIndicator/StepIndicator";
import { scrollToFirstValidationMessage } from "../../components/validationMessage/scrollToFirstValidationMessage";
import { ValidationMessage } from "../../components/validationMessage/ValidationMessage";
import { useDlpFlowContext } from "../../hooks/useDlpContext";
import useIsClient from "../../lib/useIsClient";
import { isInsuredComplete } from "../../providers/dlpFlow/DlpFlow.insured";
import { force } from "../../typeguards/force";
import { FlowLayout } from "../FlowLayout";
import styles from "./DlpFlowStepOne.module.scss";

const MAX_COVERAGES = 3;

interface Props {
  fields: Record<string, Field<string>>;
}

export const DlpFlowStepOne = (props: Props) => {
  const [loading, setLoading] = useState(false);
  const { data, goToStepTwo, modelState, setModelState, setters, premium, isLoadingPremium } = useDlpFlowContext();
  const { isClient, key } = useIsClient();

  const checkHasInsuredPersons = useCallback(() => {
    const insuredsComplete = data.insuredPersons.every(isInsuredComplete);

    const hasCoverage = data.coverages.filter((c) => c.effectiveInsured?.length !== 0).length > 0;

    return (insuredsComplete && data.hasSecondInsured !== undefined) || hasCoverage;
  }, [data]);

  const [hasInsuredPersons, setHasInsuredPersons] = useState(checkHasInsuredPersons);

  useEffect(() => {
    if (!hasInsuredPersons && checkHasInsuredPersons()) {
      setHasInsuredPersons(true);

      if (data.coverages.length === 0) {
        if (data.insuredPersons.length === 1) {
          setters.addCoverage([force(data.insuredPersons[0].personId)]);
        } else {
          setters.addCoverage([]);
        }

        scrollToCoverageItem(0);
      }
    }
  }, [data, hasInsuredPersons, checkHasInsuredPersons, setters]);

  const hasCompletedExistingCoverages =
    data.coverages.filter((c) => {
      // if the insuranceType is 'Annuity', a reductionRate is also required
      if (c.insuranceType === "Annuity") {
        return c.reductionRate !== undefined;
      }

      return c.insuranceType !== undefined;
    }).length === data.coverages.length;

  const hasLessThanMaxCoverages = data.coverages.length < MAX_COVERAGES;

  const addCoverage = () => {
    const newCoverageId = data.coverages.length;
    if (data.insuredPersons.length === 1 && data.insuredPersons[0].personId) {
      setters.addCoverage([data.insuredPersons[0].personId]);
    } else {
      setters.addCoverage([]);
    }
    scrollToCoverageItem(newCoverageId);
  };

  const scrollToCoverageItem = (coverageIndex: number) => {
    // delay scrolling, so the interface has time to update
    setTimeout(() => {
      const coverageElement = document.getElementById(getCoverageId(coverageIndex));
      if (coverageElement) {
        coverageElement.scrollIntoView();
      }
    }, 1);
  };

  const goToStep2 = async () => {
    try {
      setLoading(true);
      await goToStepTwo(props.fields.Step2Path.value);
    } catch {
      setLoading(false);
      scrollToFirstValidationMessage();
    }
  };

  return (
    <>
      <StepIndicator activeStepIndex={0}>
        <Step label={props.fields.step1Name.value} labelMobile={props.fields.step1MobileName.value} />
        <Step label={props.fields.step2Name.value} labelMobile={props.fields.step2MobileName.value} />
        <Step label={props.fields.step3Name.value} labelMobile={props.fields.step3MobileName.value} />
        <Step label={props.fields.step4Name.value} labelMobile={props.fields.step4MobileName.value} />
        <Step label={props.fields.step5Name.value} labelMobile={props.fields.step5MobileName.value} />
      </StepIndicator>
      <FlowLayout
        Sidebar={
          <DlpSummary
            {...props}
            submitButtonProps={{
              id: "button_submit_calculate",
              children: props.fields.CreateInvoiceSubmitButtonText.value,
              disabled: premium === null || isLoadingPremium,
              loading,
              onClick: goToStep2,
            }}
          />
        }
        {...props}
      >
        <ValidationMessage id="global" modelState={modelState} property="Global" />

        <InsuredPersons
          fields={props.fields}
          data={data}
          modelState={modelState}
          setModelState={setModelState}
          onUpdateInsured={setters.updateInsuredData}
          onAddInsuredPerson={setters.addInsuredPerson}
          onRemoveInsuredPerson={setters.removeInsuredPerson}
        />
        <div>
          <ValidationMessage id="coverageValidation" modelState={modelState} property="model.Coverages" />
        </div>
        {hasInsuredPersons &&
          isClient &&
          data.coverages.map((coverage, i: number) => (
            <Coverage
              key={i + key}
              fields={props.fields}
              data={data}
              coverage={coverage}
              index={i}
              onUpdateCoverageEffectiveInsured={setters.updateCoverageEffectiveInsured}
              onUpdateCoverageInsuredAmount={setters.updateCoverageInsuredAmount}
              onUpdateCoverageHasInsuredAmount={setters.updateCoverageHasInsuredAmount}
              onUpdateCoverageDuration={setters.updateCoverageDuration}
              onUpdateCoverageHasDuration={setters.updateCoverageHasDuration}
              onUpdateCoverageEffectiveDate={setters.updateCoverageEffectiveDate}
              onUpdateCoverageInsuranceType={setters.updateCoverageInsuranceType}
              onUpdateCoverageReductionRate={setters.updateCoverageReductionRate}
              onDeleteCoverage={setters.deleteCoverage}
              modelState={modelState}
              setModelState={setModelState}
            />
          ))}
        {hasInsuredPersons && hasLessThanMaxCoverages && (
          <div className={styles.dlpFlowStepOne__addCoverButtonWrapper}>
            <Button
              id="button_add_coverage"
              disabled={!hasCompletedExistingCoverages}
              type="link"
              leading={<i className="icon-plus" />}
              onClick={addCoverage}
            >
              <Text field={props.fields.AddCoverageText}></Text>
            </Button>
          </div>
        )}
      </FlowLayout>
    </>
  );
};
